import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { de as deLocale, enUS, fr as frLocale } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X as CloseIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../context/LanguageContext';

const localeMap = {
  en: enUS,
  de: deLocale,
  fr: frLocale,
};

const parseDateValue = (value) => {
  if (!value) return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const parsed = parseISO(value);
  return Number.isNaN(parsed?.getTime?.()) ? undefined : parsed;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const DatePicker = React.forwardRef(
  (
    {
      label,
      value,
      onChange,
      required = false,
      error,
      description,
      placeholder = 'Select date',
      disabled = false,
      id,
      name,
      min,
      max,
      className,
    },
    ref,
  ) => {
    const { language } = useLanguage();
    const locale = localeMap[language] || enUS;

    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const controlRef = useRef(null);

    useEffect(() => {
      if (typeof ref === 'function') {
        ref(controlRef.current);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = controlRef.current;
      }
    }, [ref]);

    const selectedDate = useMemo(() => parseDateValue(value), [value]);
    const weekOptions = useMemo(
      () => ({
        locale,
        weekStartsOn: locale?.options?.weekStartsOn ?? 1,
      }),
      [locale],
    );
    const minDate = useMemo(() => {
      const parsed = parseDateValue(min);
      return parsed ? startOfDay(parsed) : undefined;
    }, [min]);
    const maxDate = useMemo(() => {
      const parsed = parseDateValue(max);
      return parsed ? startOfDay(parsed) : undefined;
    }, [max]);

    const [currentMonth, setCurrentMonth] = useState(() => selectedDate || new Date());

    useEffect(() => {
      if (selectedDate) {
        setCurrentMonth(selectedDate);
      }
    }, [selectedDate]);

    const displayValue = selectedDate ? format(selectedDate, 'PPP', { locale }) : '';

    const emitChange = (nextDate) => {
      const formattedValue = nextDate ? format(nextDate, 'yyyy-MM-dd') : '';
      const syntheticEvent = { target: { value: formattedValue, name } };
      onChange?.(syntheticEvent, formattedValue);
    };

    const handleSelect = (date) => {
      if (!date && date !== null) return;
      const normalizedDate = date ? startOfDay(date) : null;
      if (normalizedDate && minDate && isBefore(normalizedDate, minDate)) return;
      if (normalizedDate && maxDate && isAfter(normalizedDate, maxDate)) return;
      emitChange(normalizedDate || null);
      setOpen(false);
    };

    const weekDays = useMemo(() => {
      const start = startOfWeek(new Date(), weekOptions);
      return Array.from({ length: 7 }, (_, index) => format(addDays(start, index), 'EEE', { locale }));
    }, [locale, weekOptions]);

    const monthOptions = useMemo(() =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: format(new Date(2020, index, 1), 'LLLL', { locale }),
      })),
    [locale]);

    const years = useMemo(() => {
      const currentYear = new Date().getFullYear();
      const minYear = minDate?.getFullYear?.() ?? 1900;
      const maxYear = maxDate?.getFullYear?.() ?? currentYear + 50;
      return Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
    }, [maxDate, minDate]);

    const weeks = useMemo(() => {
      const start = startOfWeek(startOfMonth(currentMonth), weekOptions);
      const end = endOfWeek(endOfMonth(currentMonth), weekOptions);
      const days = [];
      let day = start;
      while (!isAfter(day, end)) {
        days.push(day);
        day = addDays(day, 1);
      }
      const rows = [];
      for (let i = 0; i < days.length; i += 7) {
        rows.push(days.slice(i, i + 7));
      }
      return rows;
    }, [currentMonth, weekOptions]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (!open) return;
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open]);

    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [open]);

    const isDayDisabled = (day) => {
      const normalizedDay = startOfDay(day);
      if (minDate && isBefore(normalizedDay, minDate)) return true;
      if (maxDate && isAfter(normalizedDay, maxDate)) return true;
      return false;
    };

    return (
      <div className="space-y-2" ref={containerRef}>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              error ? 'text-destructive' : 'text-foreground',
            )}
          >
            {label}
            {required && <span className="ml-1 text-destructive">*</span>}
          </label>
        )}

        <div className="relative">
          <button
            type="button"
            id={id}
            ref={controlRef}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive',
              open && 'ring-2 ring-ring ring-offset-2',
              className,
            )}
            onClick={() => {
              if (!disabled) {
                setCurrentMonth(selectedDate || new Date());
                setOpen((previous) => !previous);
              }
            }}
            aria-expanded={open}
            aria-haspopup="dialog"
          >
            <span className={cn('truncate', !displayValue && 'text-muted-foreground')}>
              {displayValue || placeholder}
            </span>
            <CalendarIcon size={18} className="ml-2 h-5 w-5 text-muted-foreground" />
          </button>

          {open && (
            <div className="absolute z-50 mt-2 w-[320px] overflow-hidden rounded-md border border-border bg-popover shadow-lg">
              <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition hover:bg-muted"
                  onClick={() => setCurrentMonth((prev) => addMonths(prev, -1))}
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <select
                    className="h-8 rounded-md border border-border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={currentMonth.getMonth()}
                    onChange={(event) => {
                      const nextMonth = Number(event.target.value);
                      setCurrentMonth((previous) => new Date(previous.getFullYear(), nextMonth, 1));
                    }}
                  >
                    {monthOptions.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="h-8 rounded-md border border-border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={currentMonth.getFullYear()}
                    onChange={(event) => {
                      const nextYear = Number(event.target.value);
                      setCurrentMonth((previous) => new Date(nextYear, previous.getMonth(), 1));
                    }}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition hover:bg-muted"
                  onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="p-3">
                <div className="mb-2 grid grid-cols-7 text-center text-xs font-medium text-muted-foreground">
                  {weekDays.map((day) => (
                    <span key={day}>{day}</span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1 text-sm">
                  {weeks.map((week) =>
                    week.map((day) => {
                      const outsideMonth = !isSameMonth(day, currentMonth);
                      const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                      const isToday = isSameDay(day, new Date());
                      const disabledDay = isDayDisabled(day);

                      return (
                        <button
                          key={day.toISOString()}
                          type="button"
                          disabled={disabledDay}
                          className={cn(
                            'h-9 w-9 rounded-md border border-transparent text-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                            outsideMonth && 'text-muted-foreground/50',
                            isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                            !isSelected && !disabledDay && 'hover:bg-muted',
                            isToday && !isSelected && 'border border-primary text-primary',
                          )}
                          onClick={() => handleSelect(day)}
                        >
                          {format(day, 'd', { locale })}
                        </button>
                      );
                    }),
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-border bg-muted/40 px-3 py-2">
                <button
                  type="button"
                  className="inline-flex items-center space-x-1 text-xs font-medium text-muted-foreground transition hover:text-foreground"
                  onClick={() => {
                    emitChange(null);
                    setOpen(false);
                  }}
                >
                  <CloseIcon size={14} />
                  <span>Clear</span>
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-primary hover:text-primary/80"
                  onClick={() => setOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
